import { useState, useEffect, useRef } from 'react'
import { Table2, ArrowRight, Sparkles } from 'lucide-react'
import { PricingCalculator } from '../components/PricingCalculator'
import { PricingSummary } from '../components/PricingSummary'
import { PricingTableModal } from '../components/PricingTableModal'
import { PricingQuoteModal } from '../components/PricingQuoteModal'
import { PricingFAQ } from '../components/PricingFAQ'
import { usePricingCalculator } from '../hooks/usePricingCalculator'

function useTutorial() {
  const [step, setStep] = useState<number | null>(null)

  useEffect(() => {
    const timer = setTimeout(() => setStep(0), 600)
    return () => clearTimeout(timer)
  }, [])

  const next = () => setStep(s => (s !== null ? s + 1 : null))
  const finish = () => {
    setStep(null)
  }

  return { step, next, finish }
}

export function PricingPublicPage() {
  const calc = usePricingCalculator()
  const [tablaOpen, setTablaOpen] = useState(false)
  const [quoteOpen, setQuoteOpen] = useState(false)
  const tutorial = useTutorial()
  const leftRef = useRef<HTMLDivElement>(null)
  const rightRef = useRef<HTMLDivElement>(null)

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header Card */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center">
              <img
                src="/logo-tonal-negro.png"
                alt="Tonal"
                className="h-16 lg:h-20 w-auto mr-4 lg:mr-6"
              />
              <div>
                <h1 className="text-xl lg:text-2xl font-bold text-gray-900 mb-1 lg:mb-2">
                  Calculadora de Precios
                </h1>
                <p className="text-sm lg:text-base text-gray-600">
                  Planes flexibles para cada necesidad
                </p>
              </div>
            </div>
            <div className="flex items-center">
              <button
                onClick={() => setTablaOpen(true)}
                className="inline-flex items-center px-3 py-2 lg:px-4 lg:py-2 bg-teal-800 hover:bg-teal-900 text-white text-xs lg:text-sm font-medium rounded-lg transition-colors duration-200 shadow-sm w-full lg:w-auto justify-center"
              >
                <Table2 className="w-3 h-3 lg:w-4 lg:h-4 mr-2" />
                Tabla de Precios
              </button>
            </div>
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Columna izquierda: Calculadora + FAQ */}
          <div className="space-y-6">
            <div ref={leftRef} className={`relative transition-all duration-500 ${tutorial.step === 0 ? 'z-50 rounded-xl ring-2 ring-teal-400/50' : ''}`}>
              <PricingCalculator
                volCorreo={calc.volCorreo}
                volWhatsapp={calc.volWhatsapp}
                volCorreoWhatsapp={calc.volCorreoWhatsapp}
                setVolCorreo={calc.setVolCorreo}
                setVolWhatsapp={calc.setVolWhatsapp}
                setVolCorreoWhatsapp={calc.setVolCorreoWhatsapp}
              />
            </div>
            <PricingFAQ />
          </div>

          {/* Columna derecha: Resumen */}
          <div ref={rightRef} className={`relative transition-all duration-500 ${tutorial.step === 1 ? 'z-50 rounded-xl ring-2 ring-teal-400/50 [&_button]:hidden' : ''}`} style={tutorial.step === 1 ? { background: '#fff', borderRadius: '0.75rem' } : undefined}>
            <PricingSummary
              serviciosActivos={calc.serviciosActivos}
              precioMensual={calc.precioMensual}
              precioTotal={calc.precioTotal}
              totalEnvios={calc.totalEnvios}
              meses={calc.meses}
              setMeses={calc.setMeses}
              onSolicitar={() => setQuoteOpen(true)}
            />
          </div>
        </div>
      </div>

      {/* Tutorial Overlay */}
      {tutorial.step !== null && (
        <>
          <style>{`
            @keyframes tutorial-fade-in {
              from { opacity: 0; }
              to { opacity: 1; }
            }
            @keyframes tutorial-slide-up {
              from { opacity: 0; transform: translateY(12px); }
              to { opacity: 1; transform: translateY(0); }
            }
            @keyframes tutorial-pulse-ring {
              0% { box-shadow: 0 0 0 0 rgba(13, 148, 136, 0.3); }
              70% { box-shadow: 0 0 0 12px rgba(13, 148, 136, 0); }
              100% { box-shadow: 0 0 0 0 rgba(13, 148, 136, 0); }
            }
          `}</style>

          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40 transition-opacity duration-500"
            style={{
              backgroundColor: 'rgba(0,0,0,0.45)',
              backdropFilter: 'blur(2px)',
              animation: 'tutorial-fade-in 0.4s ease-out',
            }}
            onClick={tutorial.finish}
          />

          {/* Step 0: Sliders - tooltip a la derecha */}
          {tutorial.step === 0 && (
            <div
              className="fixed z-50 max-w-sm"
              style={{
                top: leftRef.current ? leftRef.current.getBoundingClientRect().top + 60 : '30%',
                left: leftRef.current ? leftRef.current.getBoundingClientRect().right + 40 : '55%',
                animation: 'tutorial-slide-up 0.4s ease-out',
              }}
            >
              <div
                className="bg-white rounded-2xl shadow-2xl p-6 border border-teal-100"
                style={{ animation: 'tutorial-pulse-ring 2s ease-out infinite' }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-teal-600" />
                  </div>
                  <span className="text-xs font-medium text-teal-600 uppercase tracking-wide">Paso 1 de 2</span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Selecciona tus volúmenes</h3>
                <p className="text-sm text-gray-600 mb-5">
                  Arrastra los controles o haz clic en el número para ingresar la cantidad exacta de envíos que necesitas.
                </p>
                <button
                  onClick={tutorial.next}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  Siguiente
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Step 1: Summary - tooltip a la izquierda */}
          {tutorial.step === 1 && (
            <div
              className="fixed z-50 max-w-sm"
              style={{
                top: rightRef.current ? rightRef.current.getBoundingClientRect().top + 60 : '30%',
                left: rightRef.current ? rightRef.current.getBoundingClientRect().left - 392 : '15%',
                animation: 'tutorial-slide-up 0.4s ease-out',
              }}
            >
              <div
                className="bg-white rounded-2xl shadow-2xl p-6 border border-teal-100"
                style={{ animation: 'tutorial-pulse-ring 2s ease-out infinite' }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-teal-600" />
                  </div>
                  <span className="text-xs font-medium text-teal-600 uppercase tracking-wide">Paso 2 de 2</span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Aquí verás tus totales</h3>
                <p className="text-sm text-gray-600 mb-5">
                  El resumen se actualiza en tiempo real. Cuando estés listo, solicita tu cotización personalizada.
                </p>
                <button
                  onClick={tutorial.finish}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  <Sparkles className="w-4 h-4" />
                  ¡Cotizar!
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Modales */}
      <PricingTableModal open={tablaOpen} onOpenChange={setTablaOpen} />
      <PricingQuoteModal
        open={quoteOpen}
        onOpenChange={setQuoteOpen}
        serviciosActivos={calc.serviciosActivos}
        precioTotal={calc.precioTotal}
        meses={calc.meses}
        volCorreo={calc.volCorreo}
        volWhatsapp={calc.volWhatsapp}
        volCorreoWhatsapp={calc.volCorreoWhatsapp}
      />
    </div>
  )
}
